import { useI18n } from '../../i18n'
import { useMemo, useCallback, useRef, type KeyboardEvent } from 'react'
import { editorFontStyle } from './editor-constants'
import { highlightCode } from './highlight-code.mjs'
import './code-highlight.css'
export function CodeEditor({
  value,
  onChange = () => {},
  language,
  readOnly = false,
  disabled = false,
  ariaLabel,
  invalid,
  errorId,
}: {
  value: string
  onChange?: (v: string) => void
  language: string | null
  readOnly?: boolean
  disabled?: boolean
  ariaLabel?: string
  invalid?: boolean
  errorId?: string
}) {
  const i18n = useI18n()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const preRef = useRef<HTMLPreElement>(null)

  const highlighted = useMemo(() => highlightCode(value, language), [value, language])

  const handleScroll = useCallback(() => {
    if (preRef.current && textareaRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop
      preRef.current.scrollLeft = textareaRef.current.scrollLeft
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab') {
        if (readOnly || disabled) return
        e.preventDefault()
        const ta = e.currentTarget
        const start = ta.selectionStart
        const end = ta.selectionEnd
        const next = value.substring(0, start) + '  ' + value.substring(end)
        onChange(next)
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = start + 2
        })
      }
    },
    [value, onChange, readOnly, disabled],
  )

  return (
    <div className="file-code-editor relative h-full w-full overflow-hidden">
      <pre
        ref={preRef}
        className="absolute inset-0 m-0 p-4 overflow-auto pointer-events-none"
        style={editorFontStyle}
        aria-hidden
      >
        <code
          className={language ? `hljs language-${language}` : ''}
          dangerouslySetInnerHTML={{ __html: highlighted + '\n' }}
          style={{ background: 'transparent', padding: 0, display: 'block' }}
        />
      </pre>
      <textarea
        ref={textareaRef}
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        aria-label={ariaLabel ?? (readOnly ? i18n.t("文件内容（只读）") : i18n.t("文件内容编辑器"))}
        aria-invalid={invalid}
        aria-describedby={errorId}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        wrap="off"
        className="absolute inset-0 w-full h-full resize-none p-4 bg-transparent outline-none border-0"
        style={{
          ...editorFontStyle,
          color: 'transparent',
          caretColor: 'var(--console-code-foreground)',
          WebkitTextFillColor: 'transparent',
        }}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />
    </div>
  )
}
